import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Mail,
  Phone,
  Target,
  ShieldCheck,
  Cpu,
  Layers,
  Network,
  Merge,
  Camera,
  BriefcaseMedical,
  Settings,
  Hospital,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoWithoutBackground } from "@/components/LogoWithoutBackground";
import singularLogo from "@/assets/singular-logo-new-transparent.png";
import heroBackground from "@/assets/hero-background.jpg";

/**
 * Singular Imaging Solutions — one-file React/Tailwind landing page
 * - Minimal, modern, and purpose-driven
 * - Sections: Hero, Why, Who, Advantage, Serve, Integrity, Contact, Footer
 * - Sticky nav with smooth scroll
 * - Framer Motion reveal animations
 * - Lucide icons; shadcn/ui Button
 */

const nav = [
  { label: "Our Purpose", href: "#purpose" },
  { label: "Our Foundation", href: "#foundation" },
  { label: "Our Advantage", href: "#advantage" },
  { label: "Who We Serve", href: "#serve" },
  { label: "How We Deliver", href: "#deliver" },
  { label: "Proof in Action", href: "#proof" },
  { label: "Contact", href: "#contact" },
];

const container = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

function useSmoothScroll() {
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!target) return;
      const id = target.getAttribute("href");
      if (id && id.startsWith("#")) {
        const el = document.querySelector(id);
        if (el) {
          e.preventDefault();
          window.scrollTo({
            top: (el as HTMLElement).offsetTop - 80,
            behavior: "smooth",
          });
        }
      }
    };
    document.addEventListener("click", handler as EventListener);
    return () =>
      document.removeEventListener("click", handler as EventListener);
  }, []);
}

interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

function Section({ id, children, className = "" }: SectionProps) {
  return (
    <section id={id} className={`py-12 md:py-16 ${className}`}>
      <div className="mx-auto max-w-7xl px-6">{children}</div>
    </section>
  );
}

export default function SingularImagingSolutionsLanding() {
  useSmoothScroll();

  return (
    <div className="text-foreground selection:bg-accent/20 selection:text-accent-foreground">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <a href="#hero" className="flex items-center gap-3">
            <img
              src={singularLogo}
              alt="Singular Imaging Solutions"
              className="h-16 md:h-20"
            />
          </a>
          <nav className="hidden gap-6 md:flex">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="#contact">
              <Button className="rounded-2xl">Request a Consultation</Button>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 -z-20 opacity-30"
          style={{
            backgroundImage: `url(${heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Animated Gradient Overlays */}
        <div
          className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 animate-gradient-shift"
          style={{ backgroundSize: "200% 200%" }}
        ></div>
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-40 [background:radial-gradient(60%_60%_at_50%_10%,hsl(var(--accent)/0.3),transparent_60%),radial-gradient(50%_50%_at_80%_0%,hsl(var(--secondary)/0.5),transparent_60%)] animate-float"></div>

        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 relative z-10">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="flex flex-col md:flex-row items-center gap-8 md:gap-12"
          >
            <div className="flex-shrink-0">
              <img
                src={singularLogo}
                alt="Singular Imaging Solutions"
                className="h-32 md:h-40 w-auto"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-sm font-bold uppercase tracking-widest text-blue-600">
                Precision Imaging · Payer-Aligned Performance
              </p>
              <h1 className="mt-3 text-4xl font-bold leading-tight text-foreground md:text-5xl">
                Reducing Imaging Costs Through Connected Data
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">
                We help at-risk organizations access and share imaging across
                networks, eliminating duplication, lowering total cost of care,
                and improving the patient journey.
              </p>
              <div className="mt-8 flex flex-wrap justify-center md:justify-start gap-3">
                <a href="#advantage">
                  <Button size="lg" className="rounded-2xl">
                    Discover the Advantage{" "}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#contact">
                  <Button size="lg" variant="outline" className="rounded-2xl">
                    Partner With Us
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PURPOSE */}
      <Section id="purpose" className="bg-background">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="text-3xl font-bold md:text-4xl">Our Purpose</h2>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            We make imaging data accessible across networks so at-risk
            organizations can reduce unnecessary scans, lower costs, and improve
            care coordination.
          </p>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            We do this by providing secure, HIPAA-compliant image sharing that
            connects fragmented systems and gives clinicians instant access to
            prior imaging.
          </p>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Because in value-based care, imaging delivers value only when
            shared, not repeated.
          </p>
        </motion.div>
      </Section>

      {/* FOUNDATION */}
      <Section id="foundation" className="bg-muted/30">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="text-3xl font-bold md:text-4xl">Our Foundation</h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Backed by Global Innovation and U.S. Market Mastery
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card
              icon={<Layers className="h-6 w-6" />}
              title="Singular Health Group (SHG)"
              text="Creators of advanced imaging data platforms that power secure, interoperable image access across networks."
            />
            <Card
              icon={<Network className="h-6 w-6" />}
              title="Provider Network Solutions (PNS)"
              text="Leaders in managed-care operations and risk-based contracting with deep expertise in at-risk management."
            />
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-6">
            <p className="text-muted-foreground">
              Together, we've created a new joint venture that unifies global
              imaging innovation with U.S. managed-care expertise—delivering
              connected imaging across networks, reducing unnecessary scans, and
              aligning utilization with at-risk payment models.
            </p>
          </div>
          <div className="mt-6 grid gap-6">
            <Card
              icon={<Merge className="h-6 w-6" />}
              title="Singular Imaging Solutions"
              text="The U.S. delivery engine that brings interoperable image sharing, operational alignment, and managed-care execution together for MSOs and at-risk organizations."
            />
          </div>
        </motion.div>
      </Section>

      {/* ADVANTAGE */}
      <Section id="advantage" className="bg-background">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="text-3xl font-bold md:text-4xl">
            Our Market Advantage
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Connected Imaging. Lower Costs. Better Care Coordination.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card
              icon={<Camera className="h-6 w-6" />}
              title="Interoperable Image Sharing"
              text="We connect fragmented systems so providers can instantly access prior imaging, reducing duplication and improving care coordination."
            />
            <Card
              icon={<Target className="h-6 w-6" />}
              title="Network-Wide Visibility"
              text="Real-time imaging history across networks ensures faster decisions, fewer delays, and better patient flow."
            />
            <Card
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Risk-Model Alignment"
              text="Built to support value-based care with utilization controls, quality reporting, and streamlined authorization support."
            />
            <Card
              icon={<BriefcaseMedical className="h-6 w-6" />}
              title="Cost Reduction for At-Risk Organizations"
              text="Designed to lower imaging spend by reducing unnecessary scans, minimizing leakage, and improving throughput."
            />
          </div>
        </motion.div>
      </Section>

      {/* SERVE */}
      <Section id="serve" className="bg-muted/30">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="text-3xl font-bold md:text-4xl">Who We Serve</h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Delivering value across the healthcare ecosystem.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Health Plans"
              text="Lower imaging spend and improve member outcomes by enabling shared access and reducing unnecessary scans."
            />
            <Card
              icon={<Hospital className="h-6 w-6" />}
              title="MSOs & Provider Groups"
              text="Give clinicians instant access to prior imaging across networks—reducing repeat scans, accelerating decisions, and improving care coordination."
            />
            <Card
              icon={<LinkIcon className="h-6 w-6" />}
              title="Partners & Stakeholders"
              text="Build on a transparent model designed for operational alignment, compliance, and shared performance outcomes."
            />
          </div>
        </motion.div>
      </Section>

      {/* HOW WE DELIVER */}
      <Section id="deliver" className="bg-background">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="text-3xl font-bold md:text-4xl">How We Deliver</h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Built for Trust. Structured for Results.
          </p>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            We deliver through three core capabilities:
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card
              icon={<Cpu className="h-6 w-6" />}
              title="Interoperability Technology (MFTP)"
              text="Our proprietary Medical File Transfer Protocol (MFTP) securely connects imaging systems across networks—delivering fast, reliable, HIPAA-compliant image sharing."
            />
            <Card
              icon={<Network className="h-6 w-6" />}
              title="Operational Excellence"
              text="Risk-based contracting expertise and payer-aligned workflows that ensure compliant, efficient imaging utilization."
            />
            <Card
              icon={<Merge className="h-6 w-6" />}
              title="U.S. Market Execution"
              text="Operational delivery designed for fast adoption, measurable impact, and sustained performance across healthcare networks."
            />
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-6">
            <p className="text-muted-foreground">
              This integrated approach ensures accountability, transparency, and
              enduring value for all stakeholders.
            </p>
          </div>
        </motion.div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="bg-muted/30">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="grid gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold md:text-4xl">
                Let's Redefine Imaging Access for At-Risk Care
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">
                Partner with Singular Imaging Solutions to deliver shared
                imaging access that reduces waste and strengthens value-based
                performance.
              </p>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="mt-8 grid gap-4 rounded-3xl border border-border bg-card p-6 shadow-sm"
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Full Name" required>
                    <input
                      className="input"
                      type="text"
                      placeholder="Jane Doe"
                      required
                    />
                  </Field>
                  <Field label="Work Email" required>
                    <input
                      className="input"
                      type="email"
                      placeholder="jane@org.com"
                      required
                    />
                  </Field>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Organization">
                    <input
                      className="input"
                      type="text"
                      placeholder="Organization"
                    />
                  </Field>
                  <Field label="Role">
                    <input
                      className="input"
                      type="text"
                      placeholder="Title / Role"
                    />
                  </Field>
                </div>
                <Field label="What would you like to discuss?">
                  <textarea
                    className="input min-h-[120px]"
                    placeholder="Tell us about your goals"
                  />
                </Field>
                <div className="flex items-center gap-3">
                  <Button type="submit" className="rounded-2xl">
                    Request a Consultation
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    We typically respond within 1–2 business days.
                  </p>
                </div>
              </form>
            </div>

            <div className="space-y-4">
              <InfoCard
                icon={<Mail className="h-5 w-5" />}
                title="Email"
                text="partnerships@singularimagingusa.com"
              />
              <InfoCard
                icon={<Phone className="h-5 w-5" />}
                title="Phone"
                text="(XXX) XXX-XXXX"
              />
            </div>
          </div>
        </motion.div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-background">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 md:grid-cols-3">
          <div>
            <a href="#hero" className="flex items-center gap-3">
              <img
                src={singularLogo}
                alt="Singular Imaging Solutions"
                className="h-10"
              />
            </a>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground">
              Precision Imaging. Payer-Aligned Performance. Better Care for
              At-Risk Populations.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 md:col-span-2 md:grid-cols-3">
            <FooterCol
              title="About"
              links={[
                { label: "Our Purpose", href: "#purpose" },
                { label: "Our Advantage", href: "#advantage" },
                { label: "How We Deliver", href: "#deliver" },
              ]}
            />
            <FooterCol title="Navigate" links={nav} />
            <FooterCol
              title="Connect"
              links={[
                { label: "LinkedIn", href: "https://www.linkedin.com" },
                { label: "YouTube", href: "https://www.youtube.com" },
              ]}
            />
          </div>
        </div>
        <div className="border-t border-border px-6 py-4">
          <p className="text-center text-xs text-muted-foreground">
            © 2025 Singular Imaging Solutions. A joint venture between Singular
            Health Group and Provider Network Solutions.
          </p>
        </div>
      </footer>
    </div>
  );
}

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  text: string;
}

function Feature({ icon, title, text }: FeatureProps) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-3 text-foreground">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted text-accent">
          {icon}
        </span>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="mt-3 text-muted-foreground">{text}</p>
    </div>
  );
}

interface CardProps {
  icon: React.ReactNode;
  title: string;
  text: string;
}

function Card({ icon, title, text }: CardProps) {
  return (
    <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted text-accent">
          {icon}
        </span>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      </div>
      <p className="mt-3 text-muted-foreground">{text}</p>
    </div>
  );
}

interface DiagramNodeProps {
  icon: React.ReactNode;
  label: string;
}

function DiagramNode({ icon, label }: DiagramNodeProps) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3 text-center shadow-sm">
      <div className="mx-auto mb-1 grid h-10 w-10 place-items-center rounded-xl bg-muted text-accent">
        {icon}
      </div>
      <p className="text-sm font-medium text-foreground">{label}</p>
    </div>
  );
}

function DiagramArrow() {
  return (
    <div className="hidden items-center justify-center md:flex">
      <ArrowRight className="h-6 w-6 text-muted-foreground" />
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}

function Field({ label, children, required }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}

interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  text: string;
}

function InfoCard({ icon, title, text }: InfoCardProps) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-muted text-accent">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

interface FooterColProps {
  title: string;
  links: Array<{ label: string; href?: string; target?: string }>;
}

function FooterCol({ title, links }: FooterColProps) {
  return (
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {links.map((l) => (
          <li key={l.label}>
            <a href={l.href || l.target} className="hover:text-foreground">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
